import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import ThemeProviderWrapper from "../theme/ThemeProviderWrapper";
import AuthHeader from "../components/layout/AuthHeader";
// import AuthFooter from "../components/layout/AuthFooter";
import AuthCard from "../components/layout/AuthCard";
import { useForgotPasswordMutation } from "../redux/api/authApi";
import MailIcon from "@mui/icons-material/Mail";
import { AuthRoleBadge, parseRoleFromSearch } from "../components/auth/AuthRoleBadge";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const roleKey = useMemo(
    () => parseRoleFromSearch(searchParams.get("role")),
    [searchParams]
  );

  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [isResetLinkSent, setIsResetLinkSent] = useState(false);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };
  const handleSubmit = async () => {
    if (!email) {
      // Optionally show an error if email is not entered
      return;
    }
    try {
      await forgotPassword({ email, role: roleKey }).unwrap();
      console.log("Reset link sent successfully.");
      setIsResetLinkSent(true);
    } catch (err) {
      console.error("Failed to send reset link:", err);
    }
  };

  if (isResetLinkSent) {
    return (
      <ThemeProviderWrapper>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 50% 20%, rgba(56,189,248,0.10), transparent 60%)," +
                "radial-gradient(circle at 80% 80%, rgba(168,85,247,0.10), transparent 70%)",
              zIndex: -2,
            },
            "&:after": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage:
                "linear-gradient(0deg, transparent 0, rgba(255,255,255,0.03) 1px)," +
                "linear-gradient(90deg, transparent 0, rgba(255,255,255,0.03) 1px)",
              backgroundSize: "80px 80px",
              opacity: 0.15,
              zIndex: -1,
            },
          }}
        >
          <AuthHeader />
          <AuthCard>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" fontWeight={700}>
                Reset Password
              </Typography>
              <AuthRoleBadge roleKey={roleKey} />
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              We've sent password reset instructions to your email.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                mt: 3,
              }}
            >
              <MailIcon
                sx={{
                  width: 50,
                  height: 50,
                  color: "#0ea5e9",
                }}
              />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Check Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please check your inbox for the password reset instructions.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
                onClick={() => (window.location.href = "/")}
              >
                Back to Sign In
              </Button>
            </Stack>
          </AuthCard>
          {/* <AuthFooter /> */}
        </Box>
      </ThemeProviderWrapper>
    );
  }

  return (
    <ThemeProviderWrapper>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 50% 20%, rgba(56,189,248,0.10), transparent 60%)," +
              "radial-gradient(circle at 80% 80%, rgba(168,85,247,0.10), transparent 70%)",
            zIndex: -2,
          },
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(0deg, transparent 0, rgba(255,255,255,0.03) 1px)," +
              "linear-gradient(90deg, transparent 0, rgba(255,255,255,0.03) 1px)",
            backgroundSize: "80px 80px",
            opacity: 0.15,
            zIndex: -1,
          },
        }}
      >
        <AuthHeader />
        <AuthCard>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              Reset Password
            </Typography>
            <AuthRoleBadge roleKey={roleKey} />
          </Stack>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Enter your email to receive reset instructions
          </Typography>
          <TextField
            label="Email Address"
            placeholder="you@example.com"
            fullWidth
            value={email}
            onChange={handleEmailChange}
            InputProps={{
              sx: {
                background: "rgba(255,255,255,0.05)",
                borderRadius: "10px",
              },
            }}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={() => navigate("/")}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </Stack>
        </AuthCard>
        {/* <AuthFooter /> */}
      </Box>
    </ThemeProviderWrapper>
  );
}
